import { useEffect, useRef, useState } from "react";
import { motion } from "motion/react";
import DataProductGraph from "@/components/DataProductGraph";
import GridPlane from "@/components/data-product-graph/GridPlane";
import {
  formatHeroGridRotateZOffset,
  getHeroGridRotateZOffset,
} from "@/components/data-product-graph/hero-grid-motion";
import { sceneConfig } from "@/components/data-product-graph/scene-config";
import EnterpriseControlSection from "@/components/EnterpriseControlSection";
import IntegrationsSection from "@/components/IntegrationsSection";
import NativeAIOrchestrationSection from "@/components/NativeAIOrchestrationSection";
import OperatingLeverageSection from "@/components/OperatingLeverageSection";
import PlatformSection from "@/components/PlatformSection";
import NewsletterForm from "@/components/NewsletterForm";
import SEOHead from "@/components/SEOHead";
import SoundsFamiliar from "@/components/SoundsFamiliar";
import { Typewriter } from "@/components/ui/typewriter";

const EASE = [0.25, 0.1, 0.25, 1] as const;
const HERO_SWAP_WORDS = ["track them.", "trust them.", "act on them."];

const heroStagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.11, delayChildren: 0.08 } },
};
const heroItem = {
  hidden: { opacity: 0, y: 18 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: EASE } },
};


const VideoDemo = () => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState(false);

  const handlePlay = () => {
    if (videoRef.current) {
      videoRef.current.volume = 0.3;
      videoRef.current.play();
    }
    setPlaying(true);
  };

  return (
    <div
      className="relative rounded-[40px] overflow-hidden"
      style={{
        background: "oklch(var(--surface-panel))",
        border: "1.5px solid oklch(var(--border))",
        boxShadow: "0 16px 40px oklch(0.12 0.008 75 / 0.08)",
      }}
    >
      <video
        ref={videoRef}
        src="/media/in-practice.mp4"
        poster="/media/demo-thumbnail.png"
        className="w-full block"
        style={{ aspectRatio: "3482 / 2160" }}
        controls={playing}
        onEnded={() => setPlaying(false)}
        playsInline
        preload="none"
      >
        <track kind="captions" src="/media/in-practice.en.vtt" srcLang="en" label="English" />
      </video>
      {!playing && (
        <button
          onClick={handlePlay}
          className="absolute inset-0 flex flex-col items-center justify-center gap-4 transition-opacity hover:opacity-90"
          style={{ background: "rgba(11,11,11,0.45)" }}
          aria-label="Play demo"
        >
          <div
            className="flex items-center justify-center rounded-full bg-primary"
            style={{ width: 64, height: 64 }}
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="#0B0B0B">
              <path d="M8 5v14l11-7z" />
            </svg>
          </div>
          <span className="text-white text-sm font-medium tracking-wide">Watch it in action</span>
        </button>
      )}
    </div>
  );
};

const Index = () => {
  const heroSectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const hash = window.location.hash.slice(1);
    if (hash) {
      setTimeout(() => {
        document.getElementById(hash)?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    }
  }, []);

  const setHeroGridRotateZOffset = (offset: number) => {
    heroSectionRef.current?.style.setProperty("--hero-grid-rotate-z-offset", formatHeroGridRotateZOffset(offset));
  };

  const handleHeroMouseMove = (event: React.MouseEvent<HTMLElement>) => {
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      return;
    }

    const bounds = event.currentTarget.getBoundingClientRect();
    const rotateZOffset = getHeroGridRotateZOffset({
      clientX: event.clientX,
      left: bounds.left,
      width: bounds.width,
    });

    setHeroGridRotateZOffset(rotateZOffset);
  };

  const handleHeroPointerLeave = () => {
    setHeroGridRotateZOffset(0);
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEOHead
        title="Lumient - All your documents. One system to trust them."
        description="Turn incoming documents into records your team can inspect, trust, and reuse."
        canonical="https://lumient.ai/"
      />
      <main>
      {/* ── HERO ── */}
      <section
        ref={heroSectionRef}
        className="lumient-hero-grid relative overflow-clip pt-20 lg:pt-36 flex flex-col min-h-screen"
        onMouseMove={handleHeroMouseMove}
        onMouseLeave={handleHeroPointerLeave}
      >
        <div className="hero-grid-wrap">
          <GridPlane grid={sceneConfig.grid} className="hero-grid-plane" />
        </div>
        <div className="relative px-6 lg:px-16 max-w-7xl mx-auto flex flex-col w-full md:flex-1">
          <div className="flex flex-col items-center md:flex-1">
            <motion.div
              data-testid="hero-copy-block"
              className="mx-auto flex max-w-[58rem] flex-col items-center text-center"
              variants={heroStagger}
              initial="hidden"
              animate="visible"
            >
              <motion.h1
                variants={heroItem}
                className="hero-display mx-auto mb-5 flex max-w-[18ch] flex-col items-center text-center leading-[0.95]"
              >
                <span className="block text-[0.9em] leading-[0.98] text-foreground">All your documents.</span>
                <span
                  data-testid="hero-accent-line"
                  className="mt-2 inline-flex flex-wrap items-baseline justify-center gap-x-[0.18em] gap-y-[0.02em] text-[0.82em] leading-[0.96] md:flex-nowrap"
                  style={{ color: "oklch(var(--primary))" }}
                >
                  <span className="whitespace-nowrap">One system to</span>
                  <Typewriter
                    words={HERO_SWAP_WORDS}
                    className="whitespace-nowrap"
                  />
                </span>
              </motion.h1>

              <motion.p
                variants={heroItem}
                className="mx-auto mb-6 max-w-[42rem] text-base font-light leading-relaxed text-center"
                style={{ color: "oklch(0.44 0.012 75)" }}
              >
                Turn incoming documents into records your team can inspect, trust, and reuse.
              </motion.p>
            </motion.div>

            <motion.div
              data-testid="hero-proof-block"
              className="block w-full mt-14 lg:mt-20"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.75, ease: EASE, delay: 0.18 }}
            >
              <DataProductGraph className="mx-auto" />
            </motion.div>

          </div>
        </div>

        <div
          data-testid="hero-cta-positioner"
          className="relative z-10 mt-8 flex items-center justify-center md:absolute md:bottom-[10%] md:left-1/2 md:mt-0 md:-translate-x-1/2 md:translate-y-1/2"
        >
          <motion.div
            variants={heroItem}
            initial="hidden"
            animate="visible"
          >
            <a
              href="/contact"
              className="px-8 py-3 rounded-full text-sm font-semibold tracking-wide border border-primary text-primary transition-all duration-200 hover:bg-[oklch(var(--primary))] hover:border-[oklch(var(--primary))] hover:text-white hover:shadow-[0_14px_34px_oklch(var(--primary)_/_0.24)] active:translate-y-px"
            >
              Book a Demo
            </a>
          </motion.div>
        </div>

        {/* Bottom gradient — soft bleed implies more content below */}
        <div
          className="absolute bottom-0 inset-x-0 h-12 pointer-events-none"
          style={{ background: "linear-gradient(to bottom, transparent, oklch(0.985 0.006 75))" }}
          aria-hidden="true"
        />

        {/* Scroll chevron */}
        <motion.div
          data-testid="scroll-chevron"
          className="hidden md:block absolute bottom-6 left-1/2 -translate-x-1/2 pointer-events-none"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2, duration: 0.5 }}
          aria-hidden="true"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="oklch(0.44 0.012 75)"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="animate-bounce"
          >
            <path d="M6 9l6 6 6-6" />
          </svg>
        </motion.div>
      </section>

      {/* ── SOUNDS FAMILIAR ── */}
      <SoundsFamiliar />

      {/* ── PLATFORM OVERVIEW ── */}
      <PlatformSection />

      {/* ── OPERATING LEVERAGE ── */}
      <OperatingLeverageSection />

      {/* ── USE CASE ── */}
      <section id="use-case" className="bg-background px-6 lg:px-16 py-16 lg:py-28">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-10 gap-16 lg:gap-24 items-center">
          {/* Left 40% */}
          <div className="lg:col-span-4 space-y-6">
            <p className="text-xs font-medium tracking-[0.2em] uppercase" style={{ color: "oklch(var(--primary))" }}>
              In Practice
            </p>
            <h2 className="text-4xl lg:text-5xl leading-tight text-foreground font-serif">
              Invoice reconciliation,{" "}
              <span style={{ color: "oklch(var(--primary))" }}>without the manual work.</span>
            </h2>
            <p className="text-sm leading-relaxed font-light" style={{ color: "oklch(0.47 0.015 70)" }}>
              Scout receives every incoming invoice as a PDF. An AI workflow parses each one, extracting vendor, amount, due date, and PO reference, then pulls your Stripe transaction history to match payments against what you owe.
            </p>
            <p className="text-sm leading-relaxed font-light" style={{ color: "oklch(0.47 0.015 70)" }}>
              The result is a live view of what's settled, what's overdue, and what has discrepancies that need your attention. No spreadsheet. No reopening the same PDFs. Just a structured output that updates itself as new invoices arrive.
            </p>
          </div>

          {/* Right 60% — video demo */}
          <div className="lg:col-span-6">
            <VideoDemo />
          </div>
        </div>
      </section>

      {/* ── INTEGRATIONS ── */}
      <IntegrationsSection />

      {/* ── ENTERPRISE CONTROL ── */}
      <EnterpriseControlSection />

      {/* ── NATIVE AI ORCHESTRATION ── */}
      <NativeAIOrchestrationSection />

      {/* ── FINAL CTA ── */}
      <section className="bg-surface-inverse px-6 lg:px-16 py-20 lg:py-32 relative overflow-hidden">
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none select-none">
          <img
            src="/brand/lumient-logo-mark.svg"
            className="w-[480px] h-[480px]"
            alt=""
            loading="lazy"
            style={{ opacity: 0.04, filter: "brightness(0) invert(1)" }}
          />
        </div>

        <div className="relative max-w-2xl mx-auto text-center space-y-6">
          <h2 className="text-4xl lg:text-5xl font-semibold text-surface-inverse-foreground leading-tight font-serif">
            Stop redoing work.<br />Start building on it.
          </h2>
          <p className="text-sm font-light text-[oklch(0.83_0.006_75_/_0.82)]">
            Interested in early access, new workflows, and case studies as we release them?
          </p>
          <NewsletterForm />
        </div>
      </section>
      </main>

    </div>
  );
};

export default Index;
